import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const SaasPlansAndSubscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Upgrade / Edit Subscription Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    planCode: 'PROFESSIONAL',
    status: 'ACTIVE',
    billingCycle: 'MONTHLY',
    isTrial: false,
    maxCentres: 5,
    maxUsers: 25,
    notes: 'Upgraded subscription tier via Control Center'
  });
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const [plansRes, tenantsRes] = await Promise.all([
          saasPlatformService.getPlans(),
          saasPlatformService.getTenants()
        ]);
        setPlans(plansRes.data || plansRes || []);
        const tenantList = tenantsRes.data || tenantsRes || [];
        setTenants(tenantList);
        if (tenantList.length > 0) {
          const defaultTenant = tenantList.find(t => t.tenantId === 9111) || tenantList[0];
          setSelectedTenantId(defaultTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to initialize plans & subscriptions', err);
        setError('Failed to load plans or tenant data.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;
    const fetchSub = async () => {
      setSubLoading(true);
      setError(null);
      try {
        const res = await saasPlatformService.getTenantSubscription(selectedTenantId);
        setSubscription(res.data || res);
      } catch (err) {
        console.error('Failed to load subscription', err);
        setError('Failed to load subscription for selected tenant.');
      } finally {
        setSubLoading(false);
      }
    };
    fetchSub();
  }, [selectedTenantId]);

  const handleOpenEdit = () => {
    if (subscription) {
      setEditForm({
        planCode: subscription.planCode || 'PROFESSIONAL',
        status: subscription.status || 'ACTIVE',
        billingCycle: subscription.billingCycle || 'MONTHLY',
        isTrial: Boolean(subscription.isTrial),
        maxCentres: subscription.maxCentres || 5,
        maxUsers: subscription.maxUsers || 25,
        notes: 'Updated plan tier and resource allocations'
      });
    }
    setShowEditModal(true);
  };

  const handleSaveSubscription = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSaving(true);
    setError(null);
    try {
      const selectedPlan = plans.find(p => p.planCode === editForm.planCode);
      const payload = {
        planCode: editForm.planCode,
        status: editForm.status,
        billingCycle: editForm.billingCycle,
        isTrial: editForm.isTrial,
        maxCentres: parseInt(editForm.maxCentres, 10),
        maxUsers: parseInt(editForm.maxUsers, 10),
        featuresJson: selectedPlan ? selectedPlan.featuresJson : null,
        changeNotes: editForm.notes
      };

      await saasPlatformService.updateTenantSubscription(selectedTenantId, payload);
      setSuccess(`Subscription for tenant #${selectedTenantId} updated successfully to ${editForm.planCode}!`);
      setShowEditModal(false);
      // Reload subscription
      const res = await saasPlatformService.getTenantSubscription(selectedTenantId);
      setSubscription(res.data || res);
    } catch (err) {
      console.error('Failed to update subscription', err);
      setError(err?.response?.data?.message || 'Failed to update subscription.');
    } finally {
      setSaving(false);
    }
  };

  const currentTenantObj = tenants.find(t => t.tenantId === Number(selectedTenantId));

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>💳</span> Plans, Subscriptions &amp; Feature Entitlements
          </h1>
          <p>Commercial Tiering, License Governance, Feature Limits &amp; Subscription Lifecycles</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div><strong>Subscription Alert:</strong> {error}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="saas-success-box">
          <div><strong>Success:</strong> {success}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Plan Catalogue Cards */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#0f172a' }}>
          Platform Plan Catalogue ({plans.length} Tiers)
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
          {plans.map(p => (
            <div key={p.planCode} className="saas-card" style={{ borderTop: `4px solid ${p.planCode === 'ENTERPRISE' ? '#7c3aed' : p.planCode === 'PROFESSIONAL' ? '#0284c7' : '#10b981'}` }}>
              <div className="saas-card-header">
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{p.planName}</h4>
                  <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#64748b' }}>{p.planCode}</span>
                </div>
                <span className="saas-badge saas-badge-active">
                  ₹{Number(p.monthlyPriceInr).toLocaleString()} /mo
                </span>
              </div>
              <div className="saas-card-body" style={{ fontSize: '0.8125rem' }}>
                <p style={{ color: '#475569', margin: '0 0 1rem 0' }}>{p.description}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', background: '#f8fafc', padding: '0.75rem', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Maximum Centres:</span>
                    <strong>{p.maxCentres || 'Unlimited'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Licensed Users:</span>
                    <strong>{p.maxUsers || 'Unlimited'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Included Modules:</span>
                    <strong style={{ color: '#0284c7' }}>
                      {p.planCode === 'STARTER' ? 'Pathology, Phleb' : p.planCode === 'PROFESSIONAL' ? 'Path, Rad, Phleb, Bill' : 'All Modules + B2B'}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tenant Subscription Management */}
      <div className="saas-card">
        <div className="saas-card-header">
          <div>
            <h3 style={{ margin: 0 }}>Active Tenant Subscription &amp; Entitlements</h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Inspect and configure client license tier, expiry dates and quotas
            </span>
          </div>
          <button className="saas-btn saas-btn-primary" onClick={handleOpenEdit} disabled={!subscription}>
            ⚙️ Change Plan / Renew
          </button>
        </div>

        <div className="saas-card-body">
          {/* Tenant Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', background: '#f8fafc', padding: '1rem', borderRadius: '6px' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Select Client:</label>
            <select
              className="saas-select"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              style={{ minWidth: '320px', fontWeight: 600 }}
            >
              {tenants.map(t => (
                <option key={t.tenantId} value={t.tenantId}>
                  #{t.tenantId} — {t.tenantCode} ({t.organizationName})
                </option>
              ))}
            </select>
          </div>

          {subLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>⏳ Loading subscription details...</div>
          ) : subscription ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <span style={{ fontSize: '0.75rem', color: '#0369a1', fontWeight: 600 }}>CURRENT PLAN</span>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0284c7', marginTop: '0.25rem' }}>
                    {subscription.planName || subscription.planCode}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Cycle: {subscription.billingCycle || 'MONTHLY'}
                  </span>
                </div>

                <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>LICENSE STATUS</span>
                  <div style={{ marginTop: '0.35rem' }}>
                    <span className={`saas-badge ${subscription.status === 'ACTIVE' ? 'saas-badge-active' : 'saas-badge-warning'}`}>
                      ● {subscription.status} {subscription.isTrial ? '(TRIAL)' : ''}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                    Renews: {subscription.nextRenewalDate ? new Date(subscription.nextRenewalDate).toLocaleDateString() : 'N/A'}
                  </span>
                </div>

                <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>MAX CENTRES</span>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginTop: '0.25rem' }}>
                    {subscription.maxCentres}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Authorized Branches</span>
                </div>

                <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>MAX LICENSED USERS</span>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginTop: '0.25rem' }}>
                    {subscription.maxUsers}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Active Staff Accounts</span>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.8125rem' }}>
                <strong>Enabled Enterprise Modules &amp; Entitlements:</strong>
                <p style={{ margin: '0.5rem 0 0 0', fontFamily: 'monospace', color: '#334155' }}>
                  {subscription.featuresJson || '["PATHOLOGY", "RADIOLOGY", "PHLEBOTOMY", "BILLING", "B2B_PORTAL"]'}
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
              No subscription record found. Click "Change Plan / Renew" to assign a subscription.
            </div>
          )}
        </div>
      </div>

      {/* Edit Subscription Modal */}
      {showEditModal && (
        <div className="saas-modal-backdrop" onClick={() => setShowEditModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="saas-modal-header">
              <h3>Configure Tenant Subscription: #{selectedTenantId} ({currentTenantObj?.tenantCode})</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveSubscription}>
              <div className="saas-modal-body">
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Plan Tier</label>
                  <select
                    className="saas-form-input"
                    value={editForm.planCode}
                    onChange={(e) => setEditForm({ ...editForm, planCode: e.target.value })}
                  >
                    {plans.map(p => (
                      <option key={p.planCode} value={p.planCode}>
                        {p.planName} ({p.planCode}) — ₹{Number(p.monthlyPriceInr).toLocaleString()} /mo
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Subscription Status</label>
                    <select
                      className="saas-form-input"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="TRIAL">TRIAL</option>
                      <option value="EXPIRED">EXPIRED</option>
                      <option value="CANCELLED">CANCELLED</option>
                    </select>
                  </div>
                  <div>
                    <label className="saas-form-label">Billing Cycle</label>
                    <select
                      className="saas-form-input"
                      value={editForm.billingCycle}
                      onChange={(e) => setEditForm({ ...editForm, billingCycle: e.target.value })}
                    >
                      <option value="MONTHLY">MONTHLY</option>
                      <option value="QUARTERLY">QUARTERLY</option>
                      <option value="ANNUAL">ANNUAL</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Max Allowed Centres</label>
                    <input
                      type="number"
                      className="saas-form-input"
                      value={editForm.maxCentres}
                      onChange={(e) => setEditForm({ ...editForm, maxCentres: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Max Licensed Users</label>
                    <input
                      type="number"
                      className="saas-form-input"
                      value={editForm.maxUsers}
                      onChange={(e) => setEditForm({ ...editForm, maxUsers: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Change Justification &amp; Notes</label>
                  <textarea
                    className="saas-form-input"
                    rows={2}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : '💾 Update Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasPlansAndSubscriptions;
